"use client";

import styled from "@emotion/styled";
import { m, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";

export interface OneClickSignInDialogProps {
  shouldShowPopup: boolean;
  optIn: () => void;
  optOut: () => void;
}

const DialogContainer = styled(m.div)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  marginTop: 10,
  right: 24,
  backgroundColor: theme.colors.background.inverse.secondary,
  color: theme.colors.content.inverse.primary,
  borderColor: theme.colors.border.inverse.default,
  borderWidth: theme.borders.width.default,
  padding: "12px 16px",
  borderRadius: theme.borders.radius.default,
  boxShadow: theme.shadows.hover,
  zIndex: -1,
  maxWidth: 280,
  ...theme.fonts.primaryFonts["body-s"],
  transformOrigin: "top right",
  [`@media(min-width: ${theme.maxWidths.moduleGrid})`]: {
    right: `calc(((100vw - ${theme.maxWidths.moduleGrid}) / 2) + 20px)`,
  },
}));

const ContentWrapper = styled.div({
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
});

const TextContent = styled.p({
  margin: 0,
  padding: 0,
  flexGrow: 1,
});

const CloseButton = styled.button(({ theme }) => ({
  background: "none",
  border: "none",
  color: theme.colors.content.inverse.secondary,
  cursor: "pointer",
  padding: 0,
  fontSize: 18,
  lineHeight: 1,

  "&:hover": {
    color: theme.colors.content.inverse.primary,
  },
}));

const OptInButton = styled.button(({ theme }) => ({
  background: "none",
  border: "none",
  color: theme.colors.content.inverse.primary,
  cursor: "pointer",
  padding: "8px 0 0 0",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: 4,
  ...theme.fonts.primaryFonts["body-m-bold"],

  "&:hover": {
    "span:first-of-type": {
      textDecoration: "underline",
    },
  },

  "span:last-of-type": {
    fontSize: 16,
    lineHeight: 1,
  },
}));

export const OneClickSignInDialog = ({
  optIn,
  optOut,
}: OneClickSignInDialogProps) => {
  const [isWithinScrollThreshold, setIsWithinScrollThreshold] = useState(true);
  const [hasPlayedInitialAnimation, setHasPlayedInitialAnimation] =
    useState(false);

  useEffect(() => {
    const scrollThreshold = 500;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsWithinScrollThreshold(currentScrollY < scrollThreshold);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isVisible = isWithinScrollThreshold;

  const handleAnimationComplete = (definition: any) => {
    if (!hasPlayedInitialAnimation && definition.opacity === 1) {
      setHasPlayedInitialAnimation(true);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <DialogContainer
          initial={{ opacity: 0, y: -10 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              delay: hasPlayedInitialAnimation ? 0 : 1,
              duration: 0.3,
            },
          }}
          exit={{
            opacity: 0,
            y: -10,
            transition: {
              duration: 0.2,
            },
          }}
          onAnimationComplete={handleAnimationComplete}
        >
          <ContentWrapper>
            <TextContent>
              Skip straight to the Brex login page from now on?
            </TextContent>
            <CloseButton onClick={optOut} aria-label="Close dialog">
              &times;
            </CloseButton>
          </ContentWrapper>
          <OptInButton onClick={optIn}>
            <span>Yes, always redirect me</span>
            <span>&rarr;</span>
          </OptInButton>
        </DialogContainer>
      )}
    </AnimatePresence>
  );
};
